import { formatFileSize } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@utima/ui';
import { CheckCircle2, Image as ImageIcon, Loader2, Trash } from 'lucide-react';
import { memo, useEffect } from 'react';

import type { SingleUploadingFile } from '../fileUploadTypes';

export interface FileItemProps {
  file: SingleUploadingFile;
  onDelete?: () => void;
  onClose?: () => void;
  autoCloseMs?: number;
  preview?: string;
}

/**
 * Returns a preview URL for a file. If the file is object,
 * it will create a URL from the object.
 */
function getPreviewUrl(file: File | string | undefined): string | undefined {
  if (typeof file === 'string') {
    return file;
  }

  if (file) {
    return URL.createObjectURL(file);
  }
}

export const PreviewFileItem = memo(function PreviewFileItem({
  file,
  onDelete,
  onClose,
  autoCloseMs,
  preview,
}: FileItemProps) {
  const { t } = useLingui();

  useEffect(() => {
    if (autoCloseMs && onClose && file.status === 'completed') {
      const timeout = setTimeout(() => {
        onClose?.();
      }, autoCloseMs);

      return () => clearTimeout(timeout);
    }
  }, [autoCloseMs, file.status, onClose]);

  const previewUrl = getPreviewUrl(preview ?? file?.publicUrl ?? file?.file);
  const extension = file.file?.type.split('/').pop();
  const [name] = file.file?.name.split('.') ?? [];

  return (
    <div className='flex h-16 w-full flex-row items-center justify-between rounded-lg border border-solid border-border bg-white px-4 hover:bg-gray-50'>
      <div className='flex h-full min-w-0 flex-row items-center gap-4'>
        {previewUrl ? (
          <img className='size-10 shrink-0 object-contain' src={previewUrl} />
        ) : (
          <ImageIcon className='size-10 shrink-0 text-rose-700' />
        )}
        <div className='flex min-w-0 flex-col'>
          <div className='truncate text-[0.85rem] font-medium leading-snug'>
            {name}
          </div>
          <div className='flex items-center gap-1 text-[0.7rem] leading-tight text-muted-fg'>
            <span className='shrink-0'>.{extension?.toLowerCase()}</span>
            {file.file?.size ? (
              <>
                <span className='shrink-0'>•</span>
                <span className='shrink-0'>
                  {formatFileSize(file.file.size)}
                </span>
              </>
            ) : undefined}
            {file.status === 'uploading' && (
              <span className='flex shrink-0 items-center gap-1'>
                <span>•</span>
                <Loader2 className='size-3 animate-spin' />
                <span>{t`Uploading...`}</span>
              </span>
            )}
            {file.status === 'completed' && (
              <span className='flex shrink-0 items-center gap-1'>
                <span>•</span>
                <CheckCircle2 className='size-3 text-green-600' />
                <span>{t`Uploaded`}</span>
              </span>
            )}
            {file.status === 'error' && (
              <span className='shrink-0 text-red-500'>
                {t`Error`} {file.errorMessage}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className='flex items-center gap-2'>
        {(file.status === 'completed' || file.status === 'initial') &&
          onDelete && (
            <Button
              type='button'
              variant='ghost'
              outline
              className='text-danger'
              size='icon-sm'
              onClick={onDelete}
              aria-label={t`Delete`}
            >
              <Trash className='size-4' />
            </Button>
          )}
      </div>
    </div>
  );
});
